import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';

import {Observable} from 'rxjs/Observable';
import {Subscription} from 'rxjs/Subscription';
import {isNullOrUndefined} from 'util';

import {NotificationsService, ProjectService} from '@catcore/services';
import {ActionbarItem, FileBlob} from '@catcore/types/base';
import {addOrIgnore} from '@catcore/utils';
import {ProjectDetailsService} from '@catdetails/services';
import {Actions as actionbarItems} from '@catproject/actionbar-items';
import * as FileSaver from 'file-saver';

import {Project} from '@catcore/types/api-v2';
import {BreadcrumbService} from '@catmodules/widgets/breadcrumb/services/breadcrumb.service';
import {Item} from '@catmodules/widgets/breadcrumb/types';
import {MenuItem} from '@catui/types';
import {FeatureToProjectComponent} from "@catfeatures/components/feature-to-project/feature-to-project.component";
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {ConfirmationService} from "primeng/primeng";
import {UpdateService} from "@catcore/services/update.service";
import {ServerNotificationService} from "@catcore/services/server-notification.service";

interface Breadcrumb {
  rootDisplayName: string;
  projectDisplayName?: string;
  featureDisplayName?: string;
  actionDisplayName?: string;
}

const defaultBreadcrumbs: Breadcrumb = {
  rootDisplayName: 'Projects',
  projectDisplayName: null,
  featureDisplayName: null,
  actionDisplayName: null,
};

const DELAY_STREAM_TIMEOUT = 100;

@Component({
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss']
})
export class ProjectComponent implements OnDestroy, OnInit {
  breadcrumbs: string[];
  optionItems: MenuItem[] = [];
  private subscriptions: Subscription[] = [];
  private projectSubscription: Subscription;
  private serverMsgSubscription: Subscription;
  private projectDetailsSubscription: Subscription;
  private breadcrumbServiceSubscription: Subscription;
  private currentProject$: Observable<Project>;
  private currentProject: Project;

  private lastBreadcrumbs: Breadcrumb;

  private updateSubscription: Subscription;

  displayExportDialog: boolean = false;
  displayNotes: boolean = false;

  constructor(private projectService: ProjectService,
              private projectDetailsService: ProjectDetailsService,
              private breadcrumbService: BreadcrumbService,
              private notificationService: NotificationsService,
              private modalService: ModalService,
              private updateService: UpdateService,
              private cdRef:ChangeDetectorRef,
              private serverNotificationService: ServerNotificationService) {

    this.currentProject$ = this.projectService.getCurrentProject();
  }

  ngOnInit() {
    this.updateBreadcrumbs(defaultBreadcrumbs);
    this.projectSubscription = this
      .currentProject$
      .delay(DELAY_STREAM_TIMEOUT)
      .subscribe((projectInfo: Project) => {
        this.setProject(projectInfo);

        this.lastBreadcrumbs = {...defaultBreadcrumbs};

        if (projectInfo && !isNullOrUndefined(projectInfo.name)) {
          this.lastBreadcrumbs = {...this.lastBreadcrumbs, projectDisplayName: projectInfo.name};
          this.optionItems = this.generateProjectOptions();
        } else {
          this.optionItems = [];
        }

        this.updateBreadcrumbs(this.lastBreadcrumbs);
      });

    this.projectDetailsSubscription = this.projectDetailsService
      .getProjectDetails()
      .delay(DELAY_STREAM_TIMEOUT)
      .subscribe((projectDetails) => {
        const {currentProject} = projectDetails;

        if (currentProject && currentProject.project) {
          this.lastBreadcrumbs = this.applyActionbarLabels(this.lastBreadcrumbs, actionbarItems, projectDetails);
          this.updateBreadcrumbs(this.lastBreadcrumbs);
        }
      });

    this.breadcrumbServiceSubscription = this.breadcrumbService
      .current$
      .subscribe((breadcrumbs) => {
        this.breadcrumbs = breadcrumbs.map((value: Item) => value.label);
        this.cdRef.detectChanges();
      });

    //this.serverMsgSubscription = this.serverNotificationService.messages.subscribe((msg) => {
    //  this.notificationService.success('Message from Server', msg);
    //})
  }

  ngOnDestroy() {
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
    }
    if (this.projectDetailsSubscription) {
      this.projectDetailsSubscription.unsubscribe();
    }
    if (this.breadcrumbServiceSubscription) {
      this.breadcrumbServiceSubscription.unsubscribe();
    }
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
    if (this.serverMsgSubscription) {
      this.serverMsgSubscription.unsubscribe();
    }

    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private setProject(project: Project) {
    this.currentProject = project;
  }

  private getProject(): Project {
    return this.currentProject;
  }

  private updateBreadcrumbs(breadcrumbs: Breadcrumb) {
    let parts = [];
    parts = addOrIgnore(parts, breadcrumbs.rootDisplayName);
    parts = addOrIgnore(parts, breadcrumbs.projectDisplayName);
    parts = addOrIgnore(parts, breadcrumbs.featureDisplayName);
    parts = addOrIgnore(parts, breadcrumbs.actionDisplayName);

    this.breadcrumbs = parts;
    this.breadcrumbService
      .setRoot(parts.map((value: string): Item => {
        return {label: value, value};
      }));
  }

  private createConfigs() {
    const project = this.getProject();
    this.serverNotificationService.connect();
    const subscription = this.projectService
      .createConfigs(project, this.serverNotificationService.getUserId())
      .finally(() => subscription.unsubscribe())
      .subscribe((result) => {
        if (!isNullOrUndefined(result)) {
          if (result.status === 'warn') {
            this.notificationService.warn(result.summary, result.message);
          }

          this.serverNotificationService.signalProcessAboutToStart();
        }
      });
  }

  exportConfigs(withCsdbStub: boolean = false) {
    const project = this.getProject();
    const subscription = this.projectService
      .exportConfigs(project, withCsdbStub)
      .finally(() => subscription.unsubscribe())
      .subscribe((fileBlob: FileBlob) => {
        const defaultFileName = `${project.name}.zip`;
        FileSaver.saveAs(fileBlob.blob, fileBlob.filename ? fileBlob.filename : defaultFileName);
        this.displayExportDialog = false;
      });
  }

  private showExportConfigDialog() {
    this.displayExportDialog = true;
  }

  private exportPatchFieldLinks() {
    const project = this.getProject();
    const subscription = this.projectService
      .exportPatchFieldLinks(project)
      .finally(() => subscription.unsubscribe())
      .subscribe((fileBlob: FileBlob) => {
        FileSaver.saveAs(fileBlob.blob, fileBlob.filename);
      });
  }

  private exportPatchFields() {
    const project = this.getProject();
    const subscription = this.projectService
      .exportPatchFields(project)
      .finally(() => subscription.unsubscribe())
      .subscribe((fileBlob: FileBlob) => {
        FileSaver.saveAs(fileBlob.blob, fileBlob.filename);
      });
  }

  private exportServiceList() {
    const project = this.getProject();
    const subscription = this.projectService
      .exportServiceList(project)
      .finally(() => subscription.unsubscribe())
      .subscribe((fileBlob: FileBlob) => {
        FileSaver.saveAs(fileBlob.blob, fileBlob.filename);
      });
  }

  private updateAllFeatures() {
    const project = this.getProject();
    this.serverNotificationService.connect();
    this.updateService.update(
      () => this.projectService.updateAllFeatures(project),
      () => this.projectService.refreshCurrent(),
      'project (all features)');
  }

  private openDebugConsole() {
    this.serverNotificationService.show();
  }

  private showNotes() {
    this.displayNotes = true;
  }

  private generateProjectOptions(): MenuItem[] {
    return [
      {
        label: 'Add Feature',
        sticky: true,
        command: () => {
          this.openFeatureToProjectDialog();
        },
      },
      {
        label: 'Create Config',
        sticky: true,
        command: () => {
          this.createConfigs();
        },
      },
      {
        label: 'Show Notes',
        sticky: true,
        command: () => {
          this.showNotes();
        },
      },
      {
        label: 'Export Config',
        sticky: true,
        command: () => {
          this.showExportConfigDialog();
        },
      },
      {
        label: 'Export Service List',
        sticky: false,
        command: () => {
          this.exportServiceList();
        },
      },
      {
        label: 'Update all Features',
        sticky: false,
        command: () => {
          this.updateAllFeatures();
        },
      },
      {
        label: 'Show Config Creation Log',
        sticky: false,
        command: () => {
          this.openDebugConsole();
        },
      },
    ];
  }

  /**
   * Initilize a modal dialog
   */
  private openFeatureToProjectDialog() {
    const component = this.modalService.open(FeatureToProjectComponent);
    component.componentInstance.fromFeature = false;
    component.componentInstance.project = this.getProject();
  }

  private applyActionbarLabels(updatedBreadcrumbs: Breadcrumb,
                               actionItems: ActionbarItem[],
                               projectDetailsStates) {

    const copyOfParts = {...updatedBreadcrumbs};

    const isActiveIndicatorPropertyName = 'isActive';

    actionItems.forEach((itemInfo) => {
      const propertyName = itemInfo.reactToState.property;

      let propExists = !isNullOrUndefined(itemInfo.reactToState);

      propExists = propExists && Object
        .keys(projectDetailsStates)
        .includes(itemInfo.reactToState.property);

      if (propExists && projectDetailsStates[propertyName][isActiveIndicatorPropertyName]) {
        copyOfParts.featureDisplayName = itemInfo.label;
      }
    });

    return copyOfParts;
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/modules/project/components/project/project.component.ts